import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import usePersistedFilters from "../../../hooks/usePersistedFilters";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";

const DEFAULT_FILTERS = { query: "", status: "all" };
// useAutoPageSize'ın minRows varsayılanıyla eşleşir — ölçüm öncesi placeholder.
const INITIAL_PAGE_SIZE = 5;

/**
 * Generic hook for all SystemVariable pages. Sayfalama ve filtreleme
 * SUNUCU taraflıdır (page/limit API'ye gider, total cevaptan gelir).
 *
 * @param {Object} opts
 * @param {Function} opts.listMutation   - RTK mutation hook sonucu [trigger, result]
 * @param {Function} opts.createMutation
 * @param {Function} opts.updateMutation
 * @param {Function} opts.deleteMutation  - soft delete; kayıt listelerden düşer, dokümana
 *                                          referans veren tetkik/anamnez/tedavi kayıtları korunur
 * @param {Function} [opts.bulkMutation]
 * @param {string}   opts.itemsKey        - e.g. "foods", "items", "diagnoses"
 * @param {string}   opts.idKey           - e.g. "foodId", "labTestId"
 * @param {Object}   opts.defaultForm     - e.g. { name: "", amount: "" }
 * @param {Function} opts.itemToForm      - (item) => formValues
 * @param {Function} opts.formToPayload   - (form) => apiPayload (without token/id)
 * @param {Function} [opts.onAfterBoot]   - extra async work after token is available
 * @param {string}   opts.entityLabel     - Turkish label for error msgs ("Besin", "Tahlil", etc.)
 * @param {string}   [opts.storageKey]    - sessionStorage anahtarı; verilmezse entityLabel kullanılır
 * @param {Object}   [opts.extraParams]   - her liste isteğine eklenecek ek query paramları (ör.
 *                                          { categoryId }). `undefined` değerli anahtarlar isteğe
 *                                          eklenmez. Referansı her render'da değişebilir; hook onu
 *                                          ref'te tutar, sadece serileştirilmiş değeri değişince
 *                                          yeniden fetch tetiklenir.
 */
export default function useSystemVariable({
  listMutation,
  createMutation,
  updateMutation,
  deleteMutation,
  bulkMutation,
  itemsKey,
  idKey,
  defaultForm,
  itemToForm,
  formToPayload,
  onAfterBoot,
  entityLabel,
  storageKey,
  extraParams,
}) {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [listFn, { isLoading: isListLoading }] = listMutation;
  const [createFn, { isLoading: isCreateLoading }] = createMutation;
  const [updateFn, { isLoading: isUpdateLoading }] = updateMutation;
  const [deleteFn, { isLoading: isDeleteLoading }] = deleteMutation;

  let bulkFn = null, isBulkLoading = false;
  if (bulkMutation) {
    [bulkFn, { isLoading: isBulkLoading }] = bulkMutation;
  }

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  // Filtreler sessionStorage'da, entity başına AYRI anahtarla: sekiz sayfa
  // birbirinin filtresini ezmesin.
  const [filters, setFilters] = usePersistedFilters(
    `sysVarList:${storageKey || entityLabel}:filters`,
    DEFAULT_FILTERS
  );
  const { debounced: debouncedQuery, pending: queryPending } = useDebouncedSearch(filters.query);
  const pending = { query: queryPending };

  // Çağıranın verdiği ek query paramları (ör. Diagnoses.js'in kategori
  // filtresi). Ref'te tutulur ki fetchList onu dependency olarak taşımasın
  // (aksi halde her render'da yeniden oluşan bir obje referansı fetchList'i
  // de yeniden oluşturur). Auto-fetch effect'i SERİLEŞTİRİLMİŞ değeri
  // izler — böylece kategori değişince page/status/query değişmese bile
  // yeniden fetch tetiklenir.
  const extraParamsRef = useRef(extraParams);
  useEffect(() => { extraParamsRef.current = extraParams; }, [extraParams]);
  const serializedExtraParams = JSON.stringify(extraParams || {});

  const [page, setPage] = useState(1); // 1-tabanlı
  const [pageSize, setPageSizeState] = useState(INITIAL_PAGE_SIZE);

  // pageSize gövdeden ölçülene (useAutoPageSize) kadar hiç liste isteği
  // atılmaz — aksi halde her sayfa açılışında iki istek gider: biri
  // placeholder INITIAL_PAGE_SIZE ile, biri gerçek ölçümle. `fetchPageSize
  // === null` "henüz ölçülmedi" demektir. İlk ölçüm bu değeri hemen
  // (debounce'suz) günceller ve boot fetch'i o noktada tetikler; sonraki her
  // değişim (sürükleyerek yeniden boyutlandırma) 200ms debounce edilir ki
  // sürükleme API'yi yağmalamasın.
  const hasMeasuredPageSize = useRef(false);
  const pageSizeTimerRef = useRef(null);
  const [fetchPageSize, setFetchPageSize] = useState(null);

  const setPageSize = useCallback((next) => {
    setPageSizeState(next);
    if (!hasMeasuredPageSize.current) {
      hasMeasuredPageSize.current = true;
      if (pageSizeTimerRef.current) {
        clearTimeout(pageSizeTimerRef.current);
        pageSizeTimerRef.current = null;
      }
      setFetchPageSize(next);
      return;
    }
    if (pageSizeTimerRef.current) clearTimeout(pageSizeTimerRef.current);
    pageSizeTimerRef.current = setTimeout(() => setFetchPageSize(next), 200);
  }, []);

  useEffect(
    () => () => {
      if (pageSizeTimerRef.current) clearTimeout(pageSizeTimerRef.current);
    },
    []
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [importItems, setImportItems] = useState([]);
  const [importPage, setImportPage] = useState(0);
  const [importRowsPerPage, setImportRowsPerPage] = useState(10);

  const [form, setForm] = useState({ ...defaultForm });
  const setField = useCallback((field, value) => setForm((p) => ({ ...p, [field]: value })), []);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null });

  const bootRef = useRef(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Render zamanı clamp (bkz. useUserManagement.js safePage). pageSize
  // ölçüm sonrası küçülüp büyüdüğünde (sürükleyerek yeniden boyutlandırma)
  // totalPages düşerken saklanan `page` state'i yüksek kalabilir. Clamp'i
  // türetilmiş değer olarak render sırasında yapıyoruz ki boyamadan sonra
  // (effect'te) düzeltilene kadar geçersiz bir kare (sunucudan aralık dışı
  // sayfa istemek) görünmesin.
  const safePage = Math.max(1, Math.min(page, totalPages));

  const fetchList = useCallback(
    async (overrides = {}) => {
      if (!token) return;
      setErrorMsg("");
      try {
        // extraParamsRef.current içindeki `undefined` değerli anahtarlar
        // isteğe hiç eklenmez (ör. kategori seçili değilken categoryId
        // gönderilmemeli).
        const cleanedExtra = Object.fromEntries(
          Object.entries(extraParamsRef.current || {}).filter(([, v]) => v !== undefined)
        );
        const res = await listFn({
          token,
          query: overrides.query ?? debouncedQuery,
          status: overrides.status ?? filters.status,
          page: overrides.page ?? safePage,
          limit: overrides.limit ?? pageSize,
          ...cleanedExtra,
          ...(overrides.extra || {}),
        }).unwrap();
        setItems(res?.data?.[itemsKey] || []);
        setTotal(res?.data?.pagination?.total || 0);
      } catch (e) {
        setErrorMsg(e?.data?.message || `${entityLabel} listesi alınamadı.`);
      }
    },
    [token, debouncedQuery, filters.status, safePage, pageSize, listFn, itemsKey, entityLabel]
  );

  useEffect(() => {
    if (!token || bootRef.current) return;
    bootRef.current = true;
    if (onAfterBoot) onAfterBoot(token);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ana liste isteği: filtre/durum/sayfa/ölçülmüş pageSize/extraParams
  // değişince tetiklenir. `fetchPageSize == null` iken (henüz ölçülmedi)
  // hiç istek atılmaz — bkz. yukarıdaki not. extraParams objesi her
  // render'da yeni referansla gelebileceğinden SERİLEŞTİRİLMİŞ hâli
  // izlenir (serializedExtraParams), yoksa her render'da gereksiz fetch
  // tetiklenir.
  useEffect(() => {
    if (!token || fetchPageSize == null) return;
    fetchList();
  }, [token, debouncedQuery, filters.status, safePage, fetchPageSize, serializedExtraParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!importItems.length) { setImportPage(0); return; }
    const max = Math.max(0, Math.ceil(importItems.length / importRowsPerPage) - 1);
    if (importPage > max) setImportPage(max);
  }, [importItems.length, importRowsPerPage, importPage]);

  const openCreate = useCallback(() => { setForm({ ...defaultForm }); setCreateOpen(true); }, [defaultForm]);
  const openEdit = useCallback((it) => { setEditingItem(it); setForm(itemToForm(it)); setEditOpen(true); }, [itemToForm]);

  const submitCreate = useCallback(async () => {
    if (!token) return;
    setErrorMsg("");
    try {
      await createFn({ token, ...formToPayload(form) }).unwrap();
      setCreateOpen(false);
      fetchList();
    } catch (e) { setErrorMsg(e?.data?.message || `${entityLabel} oluşturulamadı.`); }
  }, [token, form, createFn, formToPayload, fetchList, entityLabel]);

  const submitUpdate = useCallback(async () => {
    if (!token || !editingItem?._id) return;
    setErrorMsg("");
    try {
      await updateFn({ token, [idKey]: editingItem._id, ...formToPayload(form) }).unwrap();
      setEditOpen(false); setEditingItem(null);
      fetchList();
    } catch (e) { setErrorMsg(e?.data?.message || `${entityLabel} güncellenemedi.`); }
  }, [token, editingItem, form, updateFn, idKey, formToPayload, fetchList, entityLabel]);

  const openDeleteConfirm = useCallback((it) => {
    if (!it?._id || !token) return;
    setDeleteConfirm({ open: true, item: it });
  }, [token]);

  const closeDeleteConfirm = useCallback(() => setDeleteConfirm({ open: false, item: null }), []);

  const confirmDelete = useCallback(async () => {
    const { item } = deleteConfirm;
    if (!token || !item?._id) return;
    setErrorMsg("");
    try {
      await deleteFn({ token, [idKey]: item._id }).unwrap();
      fetchList(); closeDeleteConfirm();
    } catch (e) { setErrorMsg(e?.data?.message || `${entityLabel} silinemedi.`); }
  }, [token, deleteConfirm, deleteFn, idKey, fetchList, closeDeleteConfirm, entityLabel]);

  const submitImport = useCallback(async () => {
    if (!token || !bulkFn) return;
    setErrorMsg("");
    try {
      await bulkFn({ token, items: importItems }).unwrap();
      setImportOpen(false); setImportItems([]); setImportFileName(""); setImportPage(0);
      fetchList();
    } catch (e) { setErrorMsg(e?.data?.message || "İçe aktarma başarısız."); }
  }, [token, bulkFn, importItems, fetchList]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, [setFilters]);

  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
    setPage(1);
  }, [setFilters]);

  const activeCount = useMemo(() => items.filter((x) => x.isActive).length, [items]);
  const passiveCount = useMemo(() => items.filter((x) => !x.isActive).length, [items]);
  const counts = useMemo(() => ({
    total: activeCount + passiveCount,
    active: activeCount,
    inactive: passiveCount,
  }), [activeCount, passiveCount]);

  const canSubmit = useMemo(() => Boolean(form.name?.trim()), [form.name]);

  const closeImport = useCallback(() => { setImportOpen(false); setImportItems([]); setImportFileName(""); setImportPage(0); }, []);

  return {
    token, items, total, errorMsg, setErrorMsg,
    // Yazarken (debounce) de iskelet: eski liste bir an bile görünmesin.
    isListLoading: isListLoading || queryPending, isCreateLoading, isUpdateLoading, isDeleteLoading, isBulkLoading,
    filters, setFilter, clearFilters, pending,
    page: safePage, setPage, totalPages,
    pageSize, setPageSize,
    counts, activeCount, passiveCount,
    form, setField, setForm, canSubmit,
    createOpen, setCreateOpen, openCreate, submitCreate,
    editOpen, setEditOpen, editingItem, setEditingItem, openEdit, submitUpdate,
    deleteConfirm, openDeleteConfirm, closeDeleteConfirm, confirmDelete,
    importOpen, setImportOpen, importFileName, setImportFileName,
    importItems, setImportItems, importPage, setImportPage,
    importRowsPerPage, setImportRowsPerPage,
    submitImport, closeImport,
    fetchList,
  };
}
